/**
 * 1: Setting no validate or validationSchema clears out imperative error state on onBlur/onChange
 * https://github.com/jaredpalmer/formik/issues/834
 *
 * We abuse 'status' in this case
 */
import httpStatus from 'http-status';
import React, { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Col, Form } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import { DEFAULT_COUNTRY_CODE } from '@constants/constants';
import { appInsights } from '@/telemetry/client';
import { CheckboxField } from '@components/Form/CheckboxField';
import { TextField } from '@components/Form/TextField';
import { cocNumberLookup, vatNumberLookup } from '@services/vat-client';
import { DeliveryAddress } from './DeliveryAddress';
import { Address } from './Address';

export const BusinessInformation = ({ countryData }: { countryData: any[] }) => {
  const { t } = useTranslation('register');
  const [showChamberOfCommerceInput, setShowChamberOfCommerceInput] = useState(true);
  const formikContext = useFormikContext<any>();
  const countryCode = formikContext.values['countryCode'];

  useEffect(() => {
    if (countryCode) {
      try {
        const { code } = JSON.parse(countryCode);
        setShowChamberOfCommerceInput(code === DEFAULT_COUNTRY_CODE);
      } catch {
        return;
      }
    }
  }, [countryCode]);

  const doVATLookup = async (event: React.FormEvent<HTMLInputElement>) => {
    const status = { ...formikContext.status };
    delete status.vatRegistrationNr;
    formikContext.setStatus(status);

    const vatRegistrationNr = event.currentTarget.value;

    // VAT id is 4 to 15 chars long, length is checked in formschema
    if (vatRegistrationNr.length >= 4) {
      try {
        const response = await vatNumberLookup({ vatRegistrationNr });
        if (response.valid === true) {
          const { companyName, address } = response;
          const country = countryData.find((country) => country.code === address.countryCode?.toLocaleUpperCase());
          formikContext.setFieldValue('vatRegistrationNr', vatRegistrationNr.toLocaleUpperCase());
          formikContext.setFieldValue('countryCode', JSON.stringify(country));
          formikContext.setFieldValue('companyName', companyName, false);
          formikContext.setFieldValue('postalCode', address?.postalCode, false);
          formikContext.setFieldValue('houseNumber', address.houseNumber, false);
          formikContext.setFieldValue('houseNumberAddition', address.houseNumberAddition, false);
          formikContext.setFieldValue('street', address.street, false);
          formikContext.setFieldValue('city', address.city, false);
          // Due to the way React Context and useEffect work not setting this will trigger a extra address lookup
          formikContext.setFieldValue('manualAddress', true, false);
        } else {
          formikContext.setStatus({ vatRegistrationNr: 'form-errors:invalid.vat-number' }); // [1]
        }
      } catch (error: any) {
        if (error?.response?.status === httpStatus.CONFLICT) {
          formikContext.setStatus({ vatRegistrationNr: 'form-errors:invalid.vat-number' }); // [1]
        }

        appInsights.trackException({ exception: new Error(error as any) });
      }
    }
  };

  const doCocLookup = async (event: React.FormEvent<HTMLInputElement>) => {
    const status = { ...formikContext.status };
    delete status.registrationNr;
    formikContext.setStatus(status);
    const registrationNr = event.currentTarget.value;

    if (registrationNr.match(/^[0-9]{8}$/)) {
      const companyData = await cocNumberLookup({ registrationNr });
      if (companyData) {
        formikContext.setFieldValue('companyName', companyData?.companyName, false);
        formikContext.setFieldValue('postalCode', companyData?.postalCode, false);
        formikContext.setFieldValue('houseNumber', companyData.houseNumber, false);
        formikContext.setFieldValue('street', companyData.street, false);
        formikContext.setFieldValue('city', companyData.city, false);
      } else {
        formikContext.setStatus({ registrationNr: 'form-errors:matches.coc-number' });
      }
    }
  };

  const hasDeliverAddress = formikContext.values['hasDeliveryAddress'] ?? false;

  return (
    <>
      {showChamberOfCommerceInput && (
        <Form.Group>
          <Form.Row>
            <Form.Group as={Col}>
              <h2 className="h2">{t('title.chamber-of-commerce-and-vat-number')}</h2>
            </Form.Group>
          </Form.Row>
          <Form.Row>
            <Form.Group as={Col} xs={12} md={6}>
              <TextField label={t('label.coc-number')} type="text" name="registrationNr" onBlur={doCocLookup} />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6}>
              <TextField label={t('label.vat-number')} type="text" name="vatRegistrationNr" onBlur={doVATLookup} />
            </Form.Group>
          </Form.Row>
        </Form.Group>
      )}

      {!showChamberOfCommerceInput && (
        <Form.Group>
          <Form.Row>
            <Form.Group as={Col}>
              <h2 className="h2">{t('title.vat-number')}</h2>
            </Form.Group>
          </Form.Row>
          <Form.Row>
            <Form.Group as={Col} xs={12}>
              <TextField label={t('label.vat-number')} type="text" name="vatRegistrationNr" onBlur={doVATLookup} />
            </Form.Group>
          </Form.Row>
        </Form.Group>
      )}

      <Form.Group>
        <Form.Row>
          <Form.Group as={Col}>
            <h2>{t('title.business-information')}</h2>
          </Form.Group>
        </Form.Row>
        <Form.Row>
          <Form.Group as={Col} xs={12}>
            <TextField label={t('label.company-name')} type="text" name="companyName" />
          </Form.Group>
        </Form.Row>
      </Form.Group>

      <Address />

      <Form.Group>
        <Form.Row>
          <Form.Group as={Col}>
            <Form.Label>{t('title.different-shipping-address')}</Form.Label>
          </Form.Group>
        </Form.Row>
        <Form.Row>
          <Form.Group as={Col}>
            <CheckboxField label={t('label.different-shipping-address')} name="hasDeliveryAddress" custom shouldValidate={false} />
          </Form.Group>
        </Form.Row>
        {hasDeliverAddress && <DeliveryAddress />}
      </Form.Group>
    </>
  );
};
